import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, fromEvent, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LayoutUiService {
  private readonly sidebarOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly wideQuery: MediaQueryList | null = this.createWideQuery();
  readonly sidebarOpen$: Observable<boolean> = this.sidebarOpenSubject.asObservable();
  readonly isNarrow$: Observable<boolean> = this.wideQuery
    ? fromEvent<MediaQueryListEvent>(this.wideQuery, 'change').pipe(
        map(event => !event.matches),
        startWith(!this.wideQuery.matches),
        distinctUntilChanged()
      )
    : of(false);

  readonly sidebarModal$: Observable<boolean> = combineLatest([this.sidebarOpen$, this.isNarrow$]).pipe(
    map(([open, narrow]) => open && narrow),
    distinctUntilChanged()
  );

  constructor() {
    this.isNarrow$.pipe(filter(narrow => !narrow)).subscribe(() => this.closeSidebar());
  }

  get isSidebarOpen(): boolean {
    return this.sidebarOpenSubject.value;
  }

  openSidebar(): void {
    this.sidebarOpenSubject.next(true);
  }

  closeSidebar(): void {
    if (this.sidebarOpenSubject.value) {
      this.sidebarOpenSubject.next(false);
    }
  }

  toggleSidebar(): void {
    this.sidebarOpenSubject.next(!this.sidebarOpenSubject.value);
  }

  private createWideQuery(): MediaQueryList | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return null;
    }
    const breakpoint = getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-lg').trim();
    return breakpoint ? window.matchMedia(`(min-width: ${breakpoint})`) : null;
  }
}
